import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import * as Tone from 'tone';
import {
  Play, Pause, Square, Volume2, Search, Star,
  Download, ArrowLeft, Music, Clock, SkipBack, Repeat,
  Upload, FolderOpen, User, Library, Trash2, X, Check
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { AdminEditorLink } from '@/components/edit/AdminEditorLink';

const API = process.env.REACT_APP_BACKEND_URL;

// ============================================
// ROTARY KNOB COMPONENT
// ============================================
const Knob = ({ value, onChange, label, min = 0, max = 100, size = 56 }) => {
  const [isDragging, setIsDragging] = useState(false);
  const startY = useRef(0);
  const startValue = useRef(0);

  const rotation = ((value - min) / (max - min)) * 270 - 135;

  const handleMouseDown = (e) => {
    setIsDragging(true);
    startY.current = e.clientY;
    startValue.current = value;
    e.preventDefault();
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging) return;
      const delta = startY.current - e.clientY;
      const newValue = Math.min(max, Math.max(min, startValue.current + delta * 0.5));
      onChange(Math.round(newValue));
    };

    const handleMouseUp = () => setIsDragging(false);

    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, min, max, onChange]);

  return (
    <div className="flex flex-col items-center gap-1">
      <div
        className="relative cursor-ns-resize select-none"
        style={{ width: size, height: size }}
        onMouseDown={handleMouseDown}
      >
        <div 
          className="absolute inset-0 rounded-full bg-gradient-to-b from-[#4a4a4a] to-[#2a2a2a] border border-[#555] shadow-lg"
          style={{ boxShadow: 'inset 0 2px 4px rgba(255,255,255,0.1), 0 4px 8px rgba(0,0,0,0.4)' }}
        />
        <div 
          className="absolute inset-0 flex items-center justify-center"
          style={{ transform: `rotate(${rotation}deg)` }}
        >
          <div className="w-1 h-5 bg-amber-400 rounded-full" style={{ marginTop: '-12px' }} />
        </div>
        <div className="absolute inset-3 rounded-full bg-gradient-to-b from-[#3a3a3a] to-[#1a1a1a]" />
      </div>
      <span className="text-[10px] text-gray-400 font-medium tracking-wide">{label}</span>
    </div>
  );
};

// ============================================
// PIANO KEYBOARD COMPONENT
// ============================================
const PianoKeyboard = ({ onNotePlay, activeNotes = [] }) => {
  const whiteNotes = ['C', 'D', 'E', 'F', 'G', 'A', 'B'];
  const blackNotes = ['C#', 'D#', null, 'F#', 'G#', 'A#', null];
  const octaves = [3, 4, 5];

  return (
    <div className="h-16 bg-gradient-to-b from-[#1a1a1a] to-[#0a0a0a] border-t border-[#333] flex items-end justify-center overflow-hidden">
      <div className="relative flex">
        {octaves.map(octave => (
          <div key={octave} className="relative flex">
            {whiteNotes.map((note) => (
              <button
                key={`${note}${octave}`}
                onClick={() => onNotePlay?.(`${note}${octave}`)}
                className={`w-7 h-14 bg-gradient-to-b from-white to-gray-100 border border-gray-300 
                  hover:from-gray-100 hover:to-gray-200 active:from-gray-200 active:to-gray-300
                  ${activeNotes.includes(`${note}${octave}`) ? 'from-amber-100 to-amber-200' : ''}
                  rounded-b-sm transition-all duration-75`}
              />
            ))}
            <div className="absolute top-0 left-0 flex pointer-events-none">
              {blackNotes.map((note, i) => (
                note ? (
                  <button
                    key={`${note}${octave}`}
                    onClick={() => onNotePlay?.(`${note}${octave}`)}
                    className={`w-4 h-9 bg-gradient-to-b from-[#333] to-black border border-[#222]
                      hover:from-[#444] active:from-[#555]
                      ${activeNotes.includes(`${note}${octave}`) ? 'from-amber-700 to-amber-900' : ''}
                      rounded-b-sm pointer-events-auto transition-all duration-75`}
                    style={{ marginLeft: i === 0 ? '18px' : '10px', marginRight: i === 1 || i === 4 ? '18px' : '0' }}
                  />
                ) : <div key={i} className="w-7" />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================
export default function BrasilianGrooves() {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  
  // State
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLooping, setIsLooping] = useState(true);
  const [bpm, setBpm] = useState(120);
  const [masterVolume, setMasterVolume] = useState(80);
  
  // Libraries
  const [masterLibrary, setMasterLibrary] = useState([]);
  const [userLibrary, setUserLibrary] = useState([]);
  const [activeTab, setActiveTab] = useState('master'); // 'master' or 'personal'
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSample, setSelectedSample] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Upload modal
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  
  // Controls
  const [close, setClose] = useState(50);
  const [room, setRoom] = useState(30);
  const [activeNotes, setActiveNotes] = useState([]);
  
  // Refs
  const playerRef = useRef(null);
  const synthRef = useRef(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token');
    return { 'Authorization': `Bearer ${token}` };
  };

  // Load libraries
  useEffect(() => {
    loadLibraries();
  }, [isAuthenticated]);

  const loadLibraries = async () => {
    setIsLoading(true);
    try {
      // Load master library (if user has access - Tier 2+)
      if (isAuthenticated) {
        try {
          const masterRes = await axios.get(`${API}/api/batucada/master-library`, { headers: getAuthHeaders() });
          setMasterLibrary(masterRes.data);
        } catch (err) {
          console.log('Master library not accessible (Tier 1 user?)');
          setMasterLibrary([]);
        }
        
        // Load user's personal library
        try {
          const userRes = await axios.get(`${API}/api/batucada/user/library`, { headers: getAuthHeaders() });
          setUserLibrary(userRes.data);
        } catch (err) {
          console.log('User library error:', err);
          setUserLibrary([]);
        }
      }
    } catch (err) {
      console.error('Failed to load libraries:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Initialize Tone.js
  useEffect(() => {
    Tone.Transport.bpm.value = bpm;
  }, [bpm]);

  // Get current library based on active tab
  const currentLibrary = activeTab === 'master' ? masterLibrary : userLibrary;
  
  // Filter samples
  const filteredSamples = currentLibrary.filter(s =>
    s.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Play/Stop
  const togglePlay = async () => {
    await Tone.start();
    
    if (isPlaying) {
      Tone.Transport.stop();
      if (playerRef.current) {
        playerRef.current.stop();
      }
      setIsPlaying(false);
    } else {
      if (selectedSample?.file_url) {
        try {
          if (playerRef.current) {
            playerRef.current.dispose();
          }
          
          playerRef.current = new Tone.Player({
            url: selectedSample.file_url,
            loop: isLooping,
            onload: () => {
              playerRef.current.start();
              Tone.Transport.start();
              setIsPlaying(true);
            }
          }).toDestination();
          
          playerRef.current.volume.value = (masterVolume - 80) / 2;
        } catch (err) {
          console.error('Playback error:', err);
          toast.error('Failed to play sample');
        }
      } else {
        toast.info('Select a sample to play');
      }
    }
  };

  // Handle sample selection
  const handleSelectSample = (sample) => {
    setSelectedSample(sample);
    if (sample.bpm) {
      setBpm(sample.bpm);
    }
    if (isPlaying) {
      Tone.Transport.stop();
      if (playerRef.current) playerRef.current.stop();
      setIsPlaying(false);
    }
  };

  // Piano key handler
  const handleNotePlay = async (note) => {
    await Tone.start();
    if (!synthRef.current) {
      synthRef.current = new Tone.Synth().toDestination();
    }
    synthRef.current.triggerAttackRelease(note, '8n');
    setActiveNotes([note]);
    setTimeout(() => setActiveNotes([]), 200);
  };

  // Upload handlers
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files).filter(f =>
      f.name.match(/\.(wav|mp3|m4a|aiff|flac|ogg)$/i)
    );
    setUploadFiles(prev => [...prev, ...files]);
  };

  const removeUploadFile = (index) => {
    setUploadFiles(prev => prev.filter((_, i) => i !== index));
  };

  const uploadToPersonalLibrary = async () => {
    if (uploadFiles.length === 0) return;
    
    setIsUploading(true);
    let uploaded = 0;
    
    for (const file of uploadFiles) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('name', file.name);
        formData.append('bpm', '120');
        formData.append('beats', '4');
        formData.append('is_loop', 'true');
        
        await axios.post(`${API}/api/batucada/user/upload-sample`, formData, {
          headers: getAuthHeaders()
        });
        uploaded++;
      } catch (err) {
        console.error(`Failed to upload ${file.name}:`, err);
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    
    if (uploaded > 0) {
      toast.success(`Uploaded ${uploaded} sample(s) to your library`);
      setUploadFiles([]);
      setShowUploadModal(false);
      loadLibraries();
    }
    setIsUploading(false);
  };

  const deleteFromPersonalLibrary = async (sampleId) => {
    if (!window.confirm('Delete this sample from your library?')) return;
    
    try {
      await axios.delete(`${API}/api/batucada/user/samples/${sampleId}`, {
        headers: getAuthHeaders()
      });
      toast.success('Sample deleted');
      loadLibraries();
    } catch (err) {
      toast.error('Failed to delete sample');
    }
  };

  // Cleanup
  useEffect(() => {
    return () => {
      if (playerRef.current) playerRef.current.dispose();
      if (synthRef.current) synthRef.current.dispose();
    };
  }, []);

  // Require login
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#121212] text-white flex items-center justify-center">
        <div className="text-center p-8">
          <Music className="w-16 h-16 mx-auto mb-4 text-amber-500" />
          <h1 className="text-2xl font-bold mb-2">Brasilian Percussion Grooves</h1>
          <p className="text-gray-400 mb-6">Please login to access the groove library</p>
          <Link
            to="/login"
            className="px-6 py-3 bg-amber-500 text-black rounded-lg font-bold hover:bg-amber-400"
          >
            Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-[#121212] text-white flex flex-col overflow-hidden">
      {/* Header */}
      <div className="h-12 bg-[#1a1a1a] border-b border-[#333] flex items-center px-4 gap-4">
        <Link to="/" className="p-2 hover:bg-[#333] rounded-lg">
          <ArrowLeft className="w-5 h-5" />
        </Link>
        
        {/* Transport */}
        <div className="flex items-center gap-1 bg-black rounded-lg p-1">
          <button 
            onClick={() => {
              Tone.Transport.position = 0;
              if (playerRef.current) playerRef.current.seek(0);
            }}
            className="p-2 hover:bg-[#333] rounded"
          >
            <SkipBack className="w-4 h-4" />
          </button>
          <button
            onClick={togglePlay}
            className={`p-2 rounded ${isPlaying ? 'bg-amber-500 text-black' : 'hover:bg-[#333]'}`}
          >
            {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
          </button>
          <button
            onClick={() => {
              Tone.Transport.stop();
              if (playerRef.current) playerRef.current.stop();
              setIsPlaying(false);
            }}
            className="p-2 hover:bg-[#333] rounded"
          >
            <Square className="w-4 h-4" />
          </button>
          <button
            onClick={() => setIsLooping(!isLooping)}
            className={`p-2 rounded ${isLooping ? 'bg-amber-500/20 text-amber-400' : 'hover:bg-[#333]'}`}
          >
            <Repeat className="w-4 h-4" />
          </button>
        </div>

        {/* BPM */}
        <div className="flex items-center gap-2 bg-black rounded-lg px-3 py-1">
          <Clock className="w-4 h-4 text-gray-500" />
          <input
            type="number"
            value={bpm}
            onChange={(e) => setBpm(Number(e.target.value))}
            className="w-14 bg-transparent text-center font-mono text-amber-400 focus:outline-none"
            min={60}
            max={200}
          />
          <span className="text-xs text-gray-500">BPM</span>
        </div>

        <div className="flex-1" />

        {/* Volume */}
        <div className="flex items-center gap-2">
          <Volume2 className="w-4 h-4 text-gray-500" />
          <input
            type="range"
            min={0}
            max={100}
            value={masterVolume}
            onChange={(e) => setMasterVolume(Number(e.target.value))}
            className="w-24 h-1 bg-gray-700 rounded-lg appearance-none cursor-pointer"
          />
        </div>
        
        {/* User info */}
        <div className="flex items-center gap-2 text-sm text-gray-400">
          <User className="w-4 h-4" />
          <span>{user?.name || user?.email}</span>
        </div>
        
        {/* Play What You Say Link */}
        <Link
          to="/hum-to-music"
          className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-amber-500 to-orange-500 text-black rounded-lg text-sm font-bold hover:from-amber-400 hover:to-orange-400 transition-all"
        >
          <span>🎤</span>
          Play What You Say
        </Link>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left: Sample Browser */}
        <div className="w-80 bg-black/40 backdrop-blur-md border-r border-white/10 flex flex-col">
          {/* Tabs */}
          <div className="flex border-b border-white/10">
            <button
              onClick={() => setActiveTab('master')}
              className={`flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center gap-2
                ${activeTab === 'master' ? 'text-amber-400 border-b-2 border-amber-400' : 'text-gray-500 hover:text-white'}`}
            >
              <Library className="w-4 h-4" />
              Master Library
            </button>
            <button
              onClick={() => setActiveTab('personal')}
              className={`flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center gap-2
                ${activeTab === 'personal' ? 'text-amber-400 border-b-2 border-amber-400' : 'text-gray-500 hover:text-white'}`}
            >
              <User className="w-4 h-4" />
              My Library
            </button>
          </div>

          {/* Header */}
          <div className="p-3 border-b border-white/10">
            <h2 className="text-lg font-bold text-white" style={{ fontFamily: 'cursive' }}>
              Brasilian Percussion Grooves
            </h2>
            <p className="text-[10px] text-gray-500 mt-0.5">By Mestre Curtis</p>
          </div>

          {/* Search */}
          <div className="p-2 border-b border-white/10">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search instruments..."
                className="w-full bg-black/50 border border-white/10 rounded-md py-2 pl-9 pr-3 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-amber-500/50"
              />
            </div>
          </div>

          {/* Instrument Folders - Empty, ready for upload */}
          <div className="flex-1 overflow-y-auto p-4">
            <div className="text-center text-gray-500">
              <FolderOpen className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No instruments</p>
              <p className="text-xs mt-1">Upload a ZIP to add instruments</p>
            </div>
          </div>

          {/* Footer */}
          <div className="p-3 border-t border-white/10 flex items-center justify-between">
            <span className="text-xs text-gray-500">Instruments</span>
          </div>
        </div>

        {/* Center: Visual Area */}
        <div className="flex-1 flex flex-col">
          <div className="flex-1 relative overflow-hidden">
            {/* Background - Your image with labels already included */}
            <div 
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: 'url(https://www.casasamba.com/wp-content/uploads/2026/09/zzorernt_image.png)' }}
            />

            {/* Currently Playing */}
            {selectedSample && (
              <div className="absolute bottom-4 left-4 bg-black/70 backdrop-blur-sm rounded-lg px-4 py-2 z-10">
                <p className="text-amber-400 text-sm font-medium">{selectedSample.name}</p>
                <p className="text-gray-400 text-xs">{selectedSample.bpm || 120} BPM</p>
              </div>
            )}
          </div>

          {/* Controls */}
          <div className="h-24 bg-gradient-to-b from-[#2a2a2a] to-[#1a1a1a] border-t border-[#444] flex items-center justify-center gap-8 px-8">
            <Knob value={close} onChange={setClose} label="CLOSE" />
            <Knob value={room} onChange={setRoom} label="ROOM" />
            
            <div className="w-px h-16 bg-[#444]" />
            
            <div 
              className="px-6 py-3 border-2 border-dashed border-amber-500/50 rounded-lg 
                hover:border-amber-400 hover:bg-amber-500/10 transition-all cursor-pointer"
            >
              <Download className="w-5 h-5 text-amber-400 mx-auto mb-1" />
              <span className="text-xs text-amber-400 font-medium">EXPORT</span>
            </div>
          </div>

          {/* Piano */}
          <PianoKeyboard onNotePlay={handleNotePlay} activeNotes={activeNotes} />
        </div>
      </div>

      {/* Footer */}
      <div className="h-8 bg-[#0a0a0a] border-t border-[#333] flex items-center justify-between px-4">
        <span className="text-xs text-gray-600">Escola de Samba Casa Samba - Brasilian Percussion Grooves</span>
        <span className="text-xs text-gray-600">By Mestre Curtis</span>
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-stone-900 rounded-xl w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b border-stone-800">
              <h3 className="font-bold">Upload to Your Library</h3>
              <button onClick={() => setShowUploadModal(false)} className="p-1 hover:bg-stone-800 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4">
              <div 
                onClick={() => document.getElementById('user-upload-input').click()}
                className="border-2 border-dashed border-stone-700 hover:border-purple-500 rounded-lg p-8 text-center cursor-pointer transition-all"
              >
                <input
                  id="user-upload-input"
                  type="file"
                  accept=".wav,.mp3,.m4a,.aiff,.flac,.ogg"
                  multiple
                  className="hidden"
                  onChange={handleFileSelect}
                />
                <Upload className="w-10 h-10 mx-auto mb-3 text-purple-400" />
                <p className="text-sm text-purple-400 font-medium">Click to select files</p>
                <p className="text-xs text-stone-500 mt-1">WAV, MP3, M4A, AIFF, FLAC</p>
              </div>

              {uploadFiles.length > 0 && (
                <div className="mt-4 space-y-2 max-h-40 overflow-y-auto">
                  {uploadFiles.map((file, i) => (
                    <div key={i} className="flex items-center gap-2 p-2 bg-stone-800 rounded-lg">
                      <Music className="w-4 h-4 text-purple-400" />
                      <span className="flex-1 truncate text-sm">{file.name}</span>
                      <button onClick={() => removeUploadFile(i)} className="text-stone-500 hover:text-red-400">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="p-4 border-t border-stone-800 flex justify-end gap-2">
              <button
                onClick={() => setShowUploadModal(false)}
                className="px-4 py-2 text-stone-400 hover:text-white"
              >
                Cancel
              </button>
              <button
                onClick={uploadToPersonalLibrary}
                disabled={uploadFiles.length === 0 || isUploading}
                className="px-4 py-2 bg-purple-500 text-white rounded-lg font-medium hover:bg-purple-400 disabled:opacity-50"
              >
                {isUploading ? 'Uploading...' : `Upload ${uploadFiles.length} file(s)`}
              </button>
            </div>
          </div>
        </div>
      )}
      <AdminEditorLink to="/admin/dashboard#brazilian-grooves-btn" label="Edit Brasilian Grooves" />
    </div>
  );
}
